/**
 * Billing Service
 * Phase 10: Monetization
 *
 * Handles subscription management, feature access, and usage tracking.
 * Note: Stripe integration is stubbed - requires API keys for full functionality.
 */

import { supabase } from '@/lib/supabase';
import { PLAN_LIMITS, getPlanFeatures } from '@/lib/planLimits';
import type {
  PlanId,
  Plan,
  Subscription,
  Invoice,
  UsageMetrics,
  UsageLimitResult,
  FeatureKey,
  PlanFeatures,
  BillingCycle,
} from '@/types/billing.types';

type ServiceResult<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

class BillingService {
  /**
   * Get all available plans
   */
  async getPlans(): Promise<ServiceResult<Plan[]>> {
    try {
      const { data, error } = await supabase
        .from('plans')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) {
        // If plans table doesn't exist, return default plans
        return {
          success: true,
          data: this.getDefaultPlans(),
        };
      }

      return { success: true, data: data as Plan[] };
    } catch {
      return {
        success: true,
        data: this.getDefaultPlans(),
      };
    }
  }

  /**
   * Get default plans (when database table doesn't exist)
   */
  private getDefaultPlans(): Plan[] {
    return [
      {
        id: 'free',
        name: 'Gratuit',
        description: 'Pour decouvrir StockChef',
        price_monthly: null,
        price_yearly: null,
        features: PLAN_LIMITS.free,
        is_active: true,
        sort_order: 0,
      },
      {
        id: 'starter',
        name: 'Starter',
        description: 'Pour les restaurants independants',
        price_monthly: 29,
        price_yearly: 290,
        features: PLAN_LIMITS.starter,
        is_active: true,
        sort_order: 1,
      },
      {
        id: 'pro',
        name: 'Pro',
        description: 'Pour les groupes de restaurants',
        price_monthly: 59,
        price_yearly: 590,
        features: PLAN_LIMITS.pro,
        is_active: true,
        sort_order: 2,
      },
      {
        id: 'enterprise',
        name: 'Enterprise',
        description: 'Pour les chaines et grands groupes',
        price_monthly: null,
        price_yearly: null,
        features: PLAN_LIMITS.enterprise,
        is_active: true,
        sort_order: 3,
      },
    ];
  }

  /**
   * Get subscription for a restaurant
   */
  async getSubscription(restaurantId: string): Promise<Subscription | null> {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select(`
          *,
          plan:plans(*)
        `)
        .eq('restaurant_id', restaurantId)
        .single();

      if (error || !data) {
        // Return a default free subscription if none exists
        return this.createDefaultSubscription(restaurantId);
      }

      return data as Subscription;
    } catch {
      return this.createDefaultSubscription(restaurantId);
    }
  }

  /**
   * Create a default free subscription object
   */
  private createDefaultSubscription(restaurantId: string): Subscription {
    const now = new Date();
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    return {
      id: 'default-free',
      restaurant_id: restaurantId,
      plan_id: 'free',
      status: 'active',
      billing_cycle: 'monthly',
      stripe_customer_id: null,
      stripe_subscription_id: null,
      stripe_price_id: null,
      current_period_start: now.toISOString(),
      current_period_end: endOfMonth.toISOString(),
      trial_end: null,
      canceled_at: null,
      created_at: now.toISOString(),
      updated_at: now.toISOString(),
    };
  }

  /**
   * Get the current plan ID for a restaurant
   */
  async getPlanId(restaurantId: string): Promise<PlanId> {
    const subscription = await this.getSubscription(restaurantId);
    return subscription?.plan_id || 'free';
  }

  /**
   * Get features for a restaurant's current plan
   */
  async getPlanFeatures(restaurantId: string): Promise<PlanFeatures> {
    const planId = await this.getPlanId(restaurantId);
    return getPlanFeatures(planId);
  }

  /**
   * Check if a feature is available for a restaurant
   */
  async checkFeatureAccess(
    restaurantId: string,
    feature: FeatureKey
  ): Promise<boolean> {
    const features = await this.getPlanFeatures(restaurantId);
    const value = features[feature];

    // For boolean features
    if (typeof value === 'boolean') {
      return value;
    }

    // For numeric features (limits), -1 means unlimited, > 0 means available
    if (typeof value === 'number') {
      return value === -1 || value > 0;
    }

    return false;
  }

  /**
   * Check usage limit for a specific metric
   */
  async checkUsageLimit(
    restaurantId: string,
    metric: 'products' | 'users' | 'restaurants' | 'ai_requests'
  ): Promise<UsageLimitResult> {
    const planId = await this.getPlanId(restaurantId);
    const limits = PLAN_LIMITS[planId];

    // Get current usage
    const current = await this.getUsage(restaurantId, metric);

    // Map metric to limit key
    const limitKeyMap: Record<string, keyof PlanFeatures> = {
      products: 'max_products',
      users: 'max_users',
      restaurants: 'max_restaurants',
      ai_requests: 'ai_requests_monthly',
    };

    const limitKey = limitKeyMap[metric];
    const limit = (limits[limitKey] as number) || 0;

    // -1 means unlimited
    if (limit === -1) {
      return { allowed: true, current, limit: -1 };
    }

    return {
      allowed: current < limit,
      current,
      limit,
    };
  }

  /**
   * Get current usage for a metric
   */
  async getUsage(
    restaurantId: string,
    metric: 'products' | 'users' | 'restaurants' | 'ai_requests'
  ): Promise<number> {
    try {
      switch (metric) {
        case 'products': {
          const { count } = await supabase
            .from('produits')
            .select('*', { count: 'exact', head: true })
            .eq('restaurant_id', restaurantId);
          return count || 0;
        }

        case 'users': {
          // Count team members
          try {
            const { count } = await supabase
              .from('restaurant_members')
              .select('*', { count: 'exact', head: true })
              .eq('restaurant_id', restaurantId);
            return (count || 0) + 1; // +1 for owner
          } catch {
            return 1; // At least the owner
          }
        }

        case 'restaurants': {
          // Count restaurants for this owner
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return 1;

          const { count } = await supabase
            .from('restaurants')
            .select('*', { count: 'exact', head: true })
            .eq('owner_id', user.id);
          return count || 1;
        }

        case 'ai_requests': {
          // Get AI requests for current month
          const startOfMonth = new Date();
          startOfMonth.setDate(1);
          startOfMonth.setHours(0, 0, 0, 0);

          try {
            const { data } = await supabase
              .from('usage_tracking')
              .select('value')
              .eq('restaurant_id', restaurantId)
              .eq('metric', 'ai_requests')
              .gte('period_start', startOfMonth.toISOString())
              .single();

            return data?.value || 0;
          } catch {
            return 0;
          }
        }

        default:
          return 0;
      }
    } catch {
      return 0;
    }
  }

  /**
   * Get all usage metrics for a restaurant
   */
  async getUsageMetrics(restaurantId: string): Promise<UsageMetrics> {
    const [products, users, restaurants, aiRequests] = await Promise.all([
      this.getUsage(restaurantId, 'products'),
      this.getUsage(restaurantId, 'users'),
      this.getUsage(restaurantId, 'restaurants'),
      this.getUsage(restaurantId, 'ai_requests'),
    ]);

    return {
      products_count: products,
      users_count: users,
      restaurants_count: restaurants,
      ai_requests_this_month: aiRequests,
    };
  }

  /**
   * Increment usage for a metric
   */
  async incrementUsage(
    restaurantId: string,
    metric: string,
    amount: number = 1
  ): Promise<void> {
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    const endOfMonth = new Date(startOfMonth);
    endOfMonth.setMonth(endOfMonth.getMonth() + 1);

    try {
      // Try to use RPC function if available
      await supabase.rpc('increment_usage', {
        p_restaurant_id: restaurantId,
        p_metric: metric,
        p_amount: amount,
        p_period_start: startOfMonth.toISOString(),
        p_period_end: endOfMonth.toISOString(),
      });
    } catch {
      // Fallback: upsert directly
      const { data: existing } = await supabase
        .from('usage_tracking')
        .select('id, value')
        .eq('restaurant_id', restaurantId)
        .eq('metric', metric)
        .gte('period_start', startOfMonth.toISOString())
        .single();

      if (existing) {
        await supabase
          .from('usage_tracking')
          .update({ value: existing.value + amount, updated_at: new Date().toISOString() })
          .eq('id', existing.id);
      } else {
        await supabase.from('usage_tracking').insert({
          restaurant_id: restaurantId,
          metric,
          value: amount,
          period_start: startOfMonth.toISOString(),
          period_end: endOfMonth.toISOString(),
        });
      }
    }
  }

  /**
   * Get invoices for a restaurant
   */
  async getInvoices(restaurantId: string): Promise<Invoice[]> {
    try {
      const { data: subscription } = await supabase
        .from('subscriptions')
        .select('id')
        .eq('restaurant_id', restaurantId)
        .single();

      if (!subscription) return [];

      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('subscription_id', subscription.id)
        .order('created_at', { ascending: false });

      if (error) return [];

      return data as Invoice[];
    } catch {
      return [];
    }
  }

  /**
   * Check if restaurant has premium plan (Starter or above)
   */
  async isPremium(restaurantId: string): Promise<boolean> {
    const planId = await this.getPlanId(restaurantId);
    return planId !== 'free';
  }

  /**
   * Create checkout session URL (stub - requires Stripe)
   */
  async createCheckoutSession(
    restaurantId: string,
    planId: PlanId,
    billingCycle: BillingCycle,
    successUrl: string,
    cancelUrl: string
  ): Promise<string> {
    // This would normally create a Stripe checkout session
    // For now, return a placeholder URL that shows upgrade info

    // In production, this would return the Stripe checkout URL
    // For development, redirect to pricing page with info
    return `/pricing?plan=${planId}&cycle=${billingCycle}&action=checkout`;
  }

  /**
   * Create billing portal session URL (stub - requires Stripe)
   */
  async createBillingPortalSession(
    restaurantId: string,
    returnUrl: string
  ): Promise<string> {
    // This would normally create a Stripe billing portal session

    // For development, redirect to settings billing section
    return '/settings?tab=billing';
  }

  /**
   * Cancel subscription (stub - requires Stripe)
   */
  async cancelSubscription(restaurantId: string): Promise<ServiceResult<void>> {
    try {
      // In production, this would cancel via Stripe API
      // For now, just update the local subscription status
      const { error } = await supabase
        .from('subscriptions')
        .update({
          status: 'canceled',
          canceled_at: new Date().toISOString(),
        })
        .eq('restaurant_id', restaurantId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to cancel subscription',
      };
    }
  }
}

export const billingService = new BillingService();
